import { Box, styled, Tab, Tabs, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { CandidateMatch } from 'types/connectTypes';
import {
  CandidateMenuActionParams,
  CandidateNavigationMenuOptionInterface,
} from '../UserNavigationMenus/types';
import { NavigationTabMenuProps } from './types';
import { CandidatePortalDataTestIds } from 'data-testids/CandidatePortalDataTestIds';
import { TabKeys } from 'components/UserNavigationMenus/CandidateMenus/constants';

export const NavigationTabMenu: React.FC<NavigationTabMenuProps> = ({
  tabs,
  defaultActiveTab,
  orientation = 'vertical',
  className = '',
  onTabChange,
  sx,
  tabProps = {} as CandidateMenuActionParams,
  candidateMatches,
}) => {
  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab || '');

  useEffect(() => {
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    onTabChange?.(newValue);
    const tab = tabs.find((t) => t.key === newValue);
    tab?.menuItemAction?.(tabProps);
  };

  const getNotificationData = (tab: CandidateNavigationMenuOptionInterface) => {
    if (!tab.notification) return { count: 0, shouldShowNotification: false };

    const { config } = tab.notification;
    const notificationData = {
      candidateMatches,
    };
    const initialCount = config.getData(notificationData);

    if (tab.key === TabKeys.MATCHES) {
      const nonActionedPendingCount =
        candidateMatches?.pending?.filter((match: CandidateMatch) => {
          const isActioned = candidateMatches?.previously_actioned?.some(
            (actionedMatch: CandidateMatch) => actionedMatch?.id === match?.id
          );
          return !isActioned;
        }).length || 0;

      return {
        count: nonActionedPendingCount,
        shouldShowNotification: nonActionedPendingCount > 0,
      };
    }

    const shouldShow = config.shouldShow({ atMatchDashboard: false, isOnConnect: true });
    return {
      count: initialCount,
      shouldShowNotification: initialCount > 0 && shouldShow,
    };
  };

  return (
    <NavigationTabsPanelContainer
      sx={sx}
      data-testid={CandidatePortalDataTestIds.NAVIGATION_TAB_MENU}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        orientation={orientation}
        className={className}
        sx={{
          '& .MuiTabs-indicator': {
            right: 0,
            width: 3,
            backgroundColor: 'primary.main',
          },
        }}
      >
        {tabs.map(
          (tab) =>
            tab.label && (
              <StyledTab
                key={tab?.key}
                value={tab?.key}
                data-testid={tab?.dataTestId}
                label={
                  typeof tab?.label === 'string' ? (
                    <StyledLabel isSelected={activeTab === tab?.key}>{tab?.label}</StyledLabel>
                  ) : (
                    tab?.label({
                      ...getNotificationData(tab),
                      context: 'tabs',
                      isSelected: activeTab === tab?.key,
                    })
                  )
                }
                disabled={tab?.disabled}
              />
            )
        )}
      </Tabs>
    </NavigationTabsPanelContainer>
  );
};

const NavigationTabsPanelContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2, 0, 0),
  overflowY: 'auto',
}));

const StyledLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.heading7.fontSize,
  fontStyle: 'normal',
  fontWeight: isSelected ? theme.typography.fontWeightBold : theme.typography.fontWeightMedium,
  lineHeight: '28px',
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  alignItems: 'flex-start',
  padding: theme.spacing(1.5),
  paddingLeft: theme.spacing(6),
  '&.Mui-selected': {
    backgroundColor: 'action.selected',
    '& .MuiTab-wrapper': {
      fontWeight: theme.typography.fontWeightBold,
    },
  },
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '& .MuiTab-wrapper': {
    color: theme.palette.text.primary,
    fontSize: theme.typography.heading7.fontSize,
    fontStyle: 'normal',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: '28px',
  },
}));
